import { z } from 'zod'

export const MEMBERSHIP_ORG_READ_CONTRACT_VERSION = '1.0.1' as const

const numericId = z.string().regex(/^[0-9]+$/)
const permissionKey = z.string().min(1).max(160).regex(/^[a-z][a-z0-9_.:-]*$/)
const productKey = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/)
const scope = z.enum(['own', 'org', 'all'])

/** Query parsing stays deliberately permissive; handlers retain legacy validation/status precedence. */
export const membershipOrgRoleReadQuerySchema = z.object({
  includeBuiltins: z.string().optional(),
  product: z.string().optional(),
}).passthrough()

export const membershipOrgPermissionsCatalogReadQuerySchema = z.object({
  product: z.string().optional(),
}).passthrough()

const roleDefinition = z.object({
  id: numericId,
  key: z.string().min(1).max(120),
  displayName: z.string().min(1).max(120),
  product: productKey,
  isCustom: z.boolean(),
  permissions: z.array(permissionKey),
}).passthrough()

const rolePermission = z.object({
  id: numericId,
  permissionKey,
  displayName: z.string().min(1).max(160),
  scope,
}).passthrough()

const roleDetail = z.object({
  id: numericId,
  key: z.string().min(1).max(120),
  displayName: z.string().min(1).max(120),
  product: productKey,
  isCustom: z.boolean(),
  permissions: z.array(rolePermission),
}).passthrough()

const catalogPermission = z.object({
  id: numericId,
  permissionKey,
  product: productKey,
  displayName: z.string().min(1).max(160),
  description: z.string().max(500).nullable(),
  scope,
  assignableByOrgAdmin: z.boolean(),
}).passthrough()

const assignablePermission = z.object({
  id: numericId,
  permissionKey,
  scope,
  assignable: z.boolean(),
  disabledReason: z.enum([
    'MM_PERMISSION_NOT_ASSIGNABLE_BY_ORG_ADMIN',
    'MM_NO_SELF_ESCALATION',
    'MM_PRODUCT_SCOPE_MISMATCH',
  ]).optional(),
}).passthrough()

export const membershipOrgRoleDefinitionsResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ORG_READ_CONTRACT_VERSION),
  roles: z.array(roleDefinition),
}).passthrough()

export const membershipOrgRoleDetailResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ORG_READ_CONTRACT_VERSION),
  role: roleDetail,
}).passthrough()

export const membershipOrgPermissionsCatalogResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ORG_READ_CONTRACT_VERSION),
  permissions: z.array(catalogPermission),
}).passthrough()

export const membershipOrgAssignablePermissionsResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_ORG_READ_CONTRACT_VERSION),
  permissions: z.array(assignablePermission),
}).passthrough()

export type MembershipOrgRoleDefinition = z.infer<typeof roleDefinition>
export type MembershipOrgRoleDefinitionsResponse = z.infer<typeof membershipOrgRoleDefinitionsResponseSchema>
export type MembershipOrgRolePermission = z.infer<typeof rolePermission>
export type MembershipOrgRoleDetailResponse = z.infer<typeof membershipOrgRoleDetailResponseSchema>
export type MembershipOrgCatalogPermission = z.infer<typeof catalogPermission>
export type MembershipOrgPermissionsCatalogResponse = z.infer<typeof membershipOrgPermissionsCatalogResponseSchema>
export type MembershipOrgAssignablePermission = z.infer<typeof assignablePermission>
export type MembershipOrgAssignablePermissionsResponse = z.infer<typeof membershipOrgAssignablePermissionsResponseSchema>
